import { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, CircularProgress, IconButton, Tooltip,
  Select, MenuItem, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { UserRole } from '../../../shared/types.js';
import type { IScanTemplate, ICategory, ICheckPack, IScanTemplateCategory, IScanTemplateCheckPack } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';
import EmojiPicker from '../../components/EmojiPicker.js';

type TemplateForm = { internal_name: string; display_name: string; description: string; emoji: string | null };
const emptyForm = (): TemplateForm => ({ internal_name: '', display_name: '', description: '', emoji: null });

interface CatSlot { category_id: string; slot: number; is_active: boolean; selected_pack_id: number | '' }

export default function TemplatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  const [templates, setTemplates] = useState<IScanTemplate[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [checkPacks, setCheckPacks] = useState<ICheckPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<TemplateForm>(emptyForm);

  const [editTarget, setEditTarget] = useState<IScanTemplate | null>(null);
  const [editForm, setEditForm] = useState<TemplateForm>(emptyForm);

  const [configTarget, setConfigTarget] = useState<IScanTemplate | null>(null);
  const [catSlots, setCatSlots] = useState<CatSlot[]>([]);

  const [defaultConfirmTarget, setDefaultConfirmTarget] = useState<IScanTemplate | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<IScanTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IScanTemplate | null>(null);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const [tplRes, catsRes, packsRes] = await Promise.all([
      api.get<{ templates: IScanTemplate[] }>(
        '/config/scan-templates',
      ),
      api.get<{ categories: ICategory[] }>('/config/categories'),
      api.get<{ check_packs: ICheckPack[] }>('/config/check-packs'),
    ]);
    if (tplRes.success) {
      setTemplates(tplRes.data.templates);
    }
    if (catsRes.success) setCategories(catsRes.data.categories);
    if (packsRes.success) setCheckPacks(packsRes.data.check_packs);
    if (!tplRes.success) setError(tplRes.error.message);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return a.display_name.localeCompare(b.display_name, 'de');
    });
  }, [templates]);

  const handleCreate = async () => {
    const res = await api.post('/config/scan-templates', {
      internal_name: createForm.internal_name.trim(),
      display_name: createForm.display_name.trim(),
      description: createForm.description.trim() || null,
    });
    if (res.success) { setCreateOpen(false); setCreateForm(emptyForm()); loadData(true); }
    else setError(res.error.message);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const res = await api.patch(`/config/scan-templates/${editTarget.id}`, {
      display_name: editForm.display_name.trim(),
      description: editForm.description.trim() || null,
      emoji: editForm.emoji,
    });
    if (res.success) { setEditTarget(null); loadData(true); }
    else setError(res.error.message);
  };

  const handleToggleActive = async (tpl: IScanTemplate) => {
    if (tpl.is_active) {
      setDeactivateTarget(tpl);
      return;
    }
    setTemplates((prev) => prev.map((t) => t.id === tpl.id ? { ...t, is_active: true } : t));
    const res = await api.patch(`/config/scan-templates/${tpl.id}`, { is_active: true });
    if (!res.success) loadData(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const tpl = deactivateTarget;
    setDeactivateTarget(null);
    setTemplates((prev) => prev.map((t) => t.id === tpl.id ? { ...t, is_active: false } : t));
    const res = await api.patch(`/config/scan-templates/${tpl.id}`, { is_active: false });
    if (!res.success) loadData(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const tpl = deleteTarget;
    setDeleteTarget(null);
    const res = await api.delete(`/config/scan-templates/${tpl.id}`);
    if (res.success) {
      loadData(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleSetDefault = async (tpl: IScanTemplate) => {
    if (!tpl.is_active) {
      setDefaultConfirmTarget(tpl);
      return;
    }
    setTemplates((prev) => prev.map((t) => ({ ...t, is_default: t.id === tpl.id })));
    const res = await api.patch(`/config/scan-templates/${tpl.id}`, { is_default: true });
    if (!res.success) loadData(true);
  };

  const handleConfirmActivateAndDefault = async () => {
    if (!defaultConfirmTarget) return;
    const tpl = defaultConfirmTarget;
    setDefaultConfirmTarget(null);
    setTemplates((prev) => prev.map((t) => ({
      ...t,
      is_active: t.id === tpl.id ? true : t.is_active,
      is_default: t.id === tpl.id,
    })));
    await api.patch(`/config/scan-templates/${tpl.id}`, { is_active: true, is_default: true });
    loadData(true);
  };

  const handleDuplicate = async (tpl: IScanTemplate) => {
    const res = await api.post(`/config/scan-templates/${tpl.id}/duplicate`, {});
    if (res.success) loadData(true);
    else setError(res.error.message);
  };

  const openConfig = (tpl: IScanTemplate) => {
    setConfigTarget(tpl);
    const existingCats = (tpl.template_categories ?? []) as IScanTemplateCategory[];
    const existingPacks = (tpl.template_check_packs ?? []) as IScanTemplateCheckPack[];

    setCatSlots(
      categories.map((cat) => {
        const existing = existingCats.find((ec) => ec.category_id === cat.id);
        const catPacks = checkPacks.filter((p) => p.category_id === cat.id);
        const activePack = catPacks.find((p) => existingPacks.some((ep) => ep.check_pack_id === p.id && ep.is_active));
        return {
          category_id: cat.id,
          slot: existing?.slot ?? 0,
          is_active: existing?.is_active ?? false,
          selected_pack_id: activePack?.id ?? '',
        };
      }),
    );
  };

  const catName = (id: string) => categories.find((c) => c.id === id)?.display_name ?? id;

  const slotDuplicates = (() => {
    const activeWithSlots = catSlots.filter((c) => c.is_active && c.slot > 0);
    const seen = new Map<number, string>();
    const dupes = new Map<string, string>();
    for (const c of activeWithSlots) {
      if (seen.has(c.slot)) {
        const first = seen.get(c.slot)!;
        dupes.set(c.category_id, `Slot ${c.slot} ist bereits belegt durch "${catName(first)}"`);
        if (!dupes.has(first)) dupes.set(first, `Slot ${c.slot} ist doppelt vergeben`);
      } else {
        seen.set(c.slot, c.category_id);
      }
    }
    return dupes;
  })();
  const hasSlotDuplicates = slotDuplicates.size > 0;

  const handleSaveConfig = async () => {
    if (!configTarget || hasSlotDuplicates) return;
    const activeCats = catSlots.filter((c) => c.is_active);
    const packToggles = checkPacks.map((pack) => {
      const cs = catSlots.find((c) => c.category_id === pack.category_id);
      return { check_pack_id: pack.id, is_active: cs?.is_active === true && cs.selected_pack_id === pack.id };
    });
    await api.put(`/config/scan-templates/${configTarget.id}/categories`, { categories: activeCats });
    await api.put(`/config/scan-templates/${configTarget.id}/check-packs`, { check_packs: packToggles });
    setConfigTarget(null);
    loadData(true);
  };

  const updateCatSlot = (catId: string, field: 'slot' | 'is_active' | 'selected_pack_id', value: number | boolean | number | '') => {
    setCatSlots((prev) => prev.map((c) => {
      if (c.category_id !== catId) return c;
      const updated = { ...c, [field]: value };
      if (field === 'is_active' && value === false) {
        updated.selected_pack_id = '';
      }
      if (field === 'is_active' && value === true && updated.selected_pack_id === '') {
        const catPacks = checkPacks.filter((p) => p.category_id === catId);
        if (catPacks.length === 1) updated.selected_pack_id = catPacks[0].id;
      }
      return updated;
    }));
  };

  const renderEmoji = (emoji: string | null) => {
    if (!emoji) return null;
    if (emoji.startsWith('/uploads/')) {
      return <Box component="img" src={emoji} alt="" sx={{ width: 24, height: 24, objectFit: 'contain', mr: 1, verticalAlign: 'middle' }} />;
    }
    return <Box component="span" sx={{ mr: 0.5 }}>{emoji}</Box>;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Neues Template
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>Name</TableCell>
              <TableCell align="center">Kategorien</TableCell>
              <TableCell align="center">Check Packs</TableCell>
              <TableCell align="center">Default</TableCell>
              <TableCell align="center">Aktiv</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTemplates.map((tpl) => {
              const catCount = (tpl.template_categories ?? []).filter((tc) => tc.is_active).length;
              const packCount = (tpl.template_check_packs ?? []).filter((tp) => tp.is_active).length;
              return (
                <TableRow key={tpl.id} hover sx={{ opacity: tpl.is_active ? 1 : 0.6 }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {renderEmoji(tpl.emoji)}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tpl.display_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{tpl.internal_name}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center"><Chip label={catCount} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center"><Chip label={packCount} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">
                    {tpl.is_default
                      ? <Chip label="Default" size="small" sx={{ bgcolor: colors.lime, color: colors.black, fontWeight: 600 }} icon={<StarIcon />} />
                      : <Tooltip title="Als Default setzen"><IconButton size="small" onClick={() => handleSetDefault(tpl)}><StarIcon fontSize="small" sx={{ color: colors.silver }} /></IconButton></Tooltip>
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={tpl.is_active} onChange={() => handleToggleActive(tpl)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => { setEditTarget(tpl); setEditForm({ internal_name: tpl.internal_name, display_name: tpl.display_name, description: tpl.description ?? '', emoji: tpl.emoji }); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Kategorien & Packs konfigurieren">
                      <IconButton size="small" onClick={() => openConfig(tpl)}>
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplizieren">
                      <IconButton size="small" onClick={() => handleDuplicate(tpl)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton size="small" onClick={() => setDeleteTarget(tpl)}>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: colors.scoreRed }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {templates.length === 0 && (
              <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">Keine Templates vorhanden</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neues Scan Template</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Internal Name" value={createForm.internal_name} onChange={(e) => setCreateForm({ ...createForm, internal_name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })} helperText="z.B. domain_screener (lowercase, underscores)" />
          <TextField label="Display Name" value={createForm.display_name} onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.internal_name.trim() || !createForm.display_name.trim()}>Erstellen</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Template bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmojiPicker
              value={editForm.emoji}
              onChange={(emoji) => setEditForm({ ...editForm, emoji })}
              checkPackId={isAdmin ? editTarget?.id : undefined}
              onUpload={isAdmin ? async (file) => {
                if (!editTarget) return;
                const fd = new FormData();
                fd.append('emoji', file);
                const res = await api.upload<{ emoji: string }>(`/config/scan-templates/${editTarget.id}/emoji-upload`, fd);
                if (res.success) {
                  setEditForm((prev) => ({ ...prev, emoji: res.data.emoji }));
                  loadData(true);
                }
              } : undefined}
              onDeleteCustom={isAdmin ? async () => {
                if (!editTarget) return;
                const res = await api.delete(`/config/scan-templates/${editTarget.id}/emoji`);
                if (res.success) {
                  setEditForm((prev) => ({ ...prev, emoji: null }));
                  loadData(true);
                }
              } : undefined}
            />
            <Typography variant="body2" color="text.secondary">Emoji auswählen</Typography>
          </Box>
          <TextField label="Display Name" value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Default-Confirm Dialog for inactive templates */}
      <Dialog open={!!defaultConfirmTarget} onClose={() => setDefaultConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Template aktivieren?</DialogTitle>
        <DialogContent>
          <Typography>
            Das Template <strong>{defaultConfirmTarget?.display_name}</strong> ist aktuell inaktiv.
            Um es als Default zu setzen, muss es aktiviert werden.
          </Typography>
          <Typography sx={{ mt: 1 }}>Möchten Sie das Template aktivieren und als Default setzen?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDefaultConfirmTarget(null)}>Nein</Button>
          <Button variant="contained" onClick={handleConfirmActivateAndDefault}>Ja, aktivieren</Button>
        </DialogActions>
      </Dialog>

      {/* Config Dialog — categories with check pack dropdown */}
      <Dialog open={!!configTarget} onClose={() => setConfigTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Template konfigurieren — {configTarget?.display_name}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Kategorien & Check Packs
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Pro Kategorie ein Check Pack auswählen. Nur aktive Kategorien werden im Scan verwendet.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.lightGrey }}>
                <TableCell sx={{ width: 56 }}>Aktiv</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell sx={{ width: 220 }}>Check Pack</TableCell>
                <TableCell sx={{ width: 110 }}>Slot (1–3)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catSlots.map((cs) => {
                const catPacks = checkPacks.filter((p) => p.category_id === cs.category_id);
                return (
                  <TableRow key={cs.category_id} sx={{
                    ...(slotDuplicates.has(cs.category_id) ? { bgcolor: '#fff3f3' } : {}),
                  }}>
                    <TableCell>
                      <Checkbox checked={cs.is_active} onChange={(e) => updateCatSlot(cs.category_id, 'is_active', e.target.checked)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{catName(cs.category_id)}</Typography>
                      {slotDuplicates.has(cs.category_id) && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {slotDuplicates.get(cs.category_id)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={cs.selected_pack_id}
                        onChange={(e) => updateCatSlot(cs.category_id, 'selected_pack_id', e.target.value as number | '')}
                        displayEmpty
                        disabled={!cs.is_active}
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">— Kein Pack —</Typography>
                        </MenuItem>
                        {catPacks.map((pack) => (
                          <MenuItem key={pack.id} value={pack.id}>
                            {pack.emoji?.startsWith('/uploads/')
                              ? <Box component="img" src={pack.emoji} alt="" sx={{ width: 18, height: 18, mr: 0.75, verticalAlign: 'middle' }} />
                              : pack.emoji ? <Box component="span" sx={{ mr: 0.5 }}>{pack.emoji}</Box> : null
                            }
                            {pack.display_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {cs.is_active && catPacks.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Keine Check Packs vorhanden
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select size="small" value={cs.slot} onChange={(e) => updateCatSlot(cs.category_id, 'slot', Number(e.target.value))} sx={{ minWidth: 80 }}>
                        <MenuItem value={0}>—</MenuItem>
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigTarget(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveConfig} disabled={hasSlotDuplicates}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Template deaktivieren?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deactivateTarget?.display_name}</strong> wirklich deaktivieren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeactivate}>Deaktivieren</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Template löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deleteTarget?.display_name}</strong> wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Endgültig löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
